import React from 'react';
import * as ScrollAreaPrimitive from '@radix-ui/react-scroll-area';
import { cn } from '@/lib/utils';

export interface ConsultationScrollAreaProps {
  className?: string;
  children: React.ReactNode;
}

/**
 * Scroll container with a short, fixed-length thumb (matching the Figma
 * consultation cards) instead of a native scrollbar, whose thumb length the
 * browser computes from the content/viewport ratio and can grow to nearly
 * the full track height when content only slightly overflows.
 */
export function ConsultationScrollArea({ className, children }: ConsultationScrollAreaProps) {
  return (
    <ScrollAreaPrimitive.Root type="always" className={cn('relative overflow-hidden', className)}>
      <ScrollAreaPrimitive.Viewport className="h-full w-full rounded-[inherit]">
        {children}
      </ScrollAreaPrimitive.Viewport>
      {/*
        Card corners using this component are rounded-3xl/rounded-[24px]. The
        scrollbar sits just inside that radius, so its vertical padding must
        clear the corner's curve (>=24px) — otherwise the thumb's top/bottom
        tip pokes past the rounded edge into the page background behind it.
      */}
      <ScrollAreaPrimitive.Scrollbar
        orientation="vertical"
        className="flex w-[4px] touch-none select-none bg-transparent py-[26px] pr-[6px] transition-colors"
      >
        {/*
          Content stays wherever it actually is (starts at the top, untouched)
          — only this visual indicator is pinned to the track's center as a
          decorative marker rather than a real scroll-position readout. Radix
          imperatively re-sets an inline `transform: translate3d(...)` on
          every scroll event, so only a stylesheet `!important` rule can
          keep it from following that.
        */}
        <ScrollAreaPrimitive.Thumb className="absolute left-0 top-1/2 !h-[32px] !w-[4px] flex-none rounded-full bg-[#BCBCBE] ![transform:translateY(-50%)] hover:bg-[#A5A5A7]" />
      </ScrollAreaPrimitive.Scrollbar>
      <ScrollAreaPrimitive.Corner />
    </ScrollAreaPrimitive.Root>
  );
}
